import { FunctionComponent, useState } from "react";
import "./styles.scss";
import Label from "../Label";
import { Icon } from "../Icon";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import ProductCard from "./ProductCard";
import Loading from "../common/Loading";
import Button from "../common/Button";
import useDevice from "../../utils/hooks/useDevice";
import classNames from "classnames";
import { displayMoney } from "../../utils/displayMoney";
import useModal from "../../utils/hooks/useModal";
import { clearAllBodyScrollLocks } from "body-scroll-lock";

type Props = {
  handleClose: () => void;
  type: "productAdded" | "AllProductsAdded";
  productLoading: () => boolean;
};

const CartModal: FunctionComponent<Props> = ({
  handleClose,
  type,
  productLoading,
}) => {
  const { isMobile } = useDevice();
  const productAdded = type === "productAdded";
  const allProductsAdded = type === "AllProductsAdded";
  const navigate = useNavigate();
  const { cartModalData, cart, lastProductAdded } = useCart();
  const totalWeight = `${cart?.summary?.totals?.weight?.toFixed(2) ?? 0} gr.`;
  const [showAlert, setShowAlert] = useState(false);
  const { isOpen, toggle } = useModal();

  const newProduct = lastProductAdded
    ? lastProductAdded
    : { title: "", thumbnailUrl: "", variations: 0 };

  const totalManufacturingCost = displayMoney(
    cart?.summary.totals.manufacturingCost ?? 0
  );

  const productIsLoading = productLoading();

  const emptyState =
    cartModalData.length === 0 && !productIsLoading && !productAdded;

  const handleCloseModal = () => {
    clearAllBodyScrollLocks();
    setShowAlert(false);
    toggle();
  };

  const { clearCart } = useCart();

  const handleOnDelete = () => {
    clearCart();
    handleCloseModal();
    setShowAlert(false);
  };

  return (
    <div
      className={classNames("cart-modal", { "cart-modal--mobile": isMobile })}
    >
      <div className="cart-modal__header">
        <div className="cart-modal__header__title">
          {productAdded ? (
            <Icon iconName="check_icon" className="cart-modal__header__icon" />
          ) : null}
          <Label
            tag="p"
            variants="body2"
            className="cart-modal__header__title__text"
          >
            {productAdded ? "AGREGADO A TU CARRITO" : "TU CARRITO DE COMPRA"}
          </Label>
        </div>
        <div onClick={handleClose} className="cart-modal__close">
          <Icon iconName="close_icon" className="cart-modal__close--icon" />
        </div>
      </div>
      <div className="cart-modal__body">
        {emptyState && (
          <Label
            tag="p"
            variants="subtitle"
            style={{ color: "black", fontFamily: "Mont-Semibold" }}
          >
            No hay productos en el carrito
          </Label>
        )}
        {productIsLoading ? (
          <Loading className={"cart-modal__body--loading"} />
        ) : null}
        {cartModalData.length > 0 &&
          !productIsLoading &&
          allProductsAdded &&
          cartModalData?.map((x, index) => (
            <ProductCard
              key={index}
              title={x.title}
              thumbnailUrl={x.thumbnailUrl}
              variations={x.variations}
            />
          ))}
        {cartModalData.length > 0 && !productIsLoading && productAdded && (
          <ProductCard
            title={newProduct.title}
            thumbnailUrl={newProduct.thumbnailUrl}
            variations={newProduct.variations}
          />
        )}
      </div>
      <div className="cart-modal__footer">
        {!emptyState && !productIsLoading ? (
          <>
            {allProductsAdded && (
              <div className="cart-modal__products-size">
                <Label tag="p" variants="body1">
                  {cartModalData.length === 1
                    ? "1 producto"
                    : `${cartModalData.length} productos`}
                </Label>
              </div>
            )}
            <hr className="cart-modal__products-size--line" />
            <div className="cart-modal__footer--subtotal">
              <Label tag="p" variants="body1">
                Subtotal
              </Label>
              <Label tag="p" variants="body1" style={{ color: "#3E3B4C" }}>
                {totalWeight}
              </Label>
              <Label tag="p" variants="body1" style={{ color: "#3E3B4C" }}>
                {totalManufacturingCost}
              </Label>
            </div>
          </>
        ) : null}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          alignContent: "space-between",
          gap: "1rem",
        }}>
          <Button
            disabled={emptyState || productIsLoading}
            className={"cart-modal__footer--button"}
            onClick={() => {
              navigate("/cart");
              handleClose();
            }}
          >
            Ver carrito {productAdded ? `(${cartModalData.length})` : ""}
          </Button>
          {!emptyState && !productIsLoading && !showAlert && (
            <a onClick={() => setShowAlert(true)} className="clean_cart">
              Vaciar carrito
            </a>
          )}
        </div>

        {showAlert && (
          <div className="alert">
            <Label tag="p" variants="body2">
              ¿Estás seguro de vaciar el carrito?
            </Label>
            <div className="remove-item-modal__buttons clean_buttons">
              <button
                className="cancel_remove"
                onClick={() => setShowAlert(false)}
              >
                Cancelar
              </button>
              <button className="remove_items" onClick={handleOnDelete}>
                Si
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartModal;
